// ResultEntry.jsx - COMPLETE FIXED VERSION
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Snackbar,
  Tooltip,
  Divider,
  Menu,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Autocomplete,
  Avatar,
  InputAdornment,
} from '@mui/material';
import {
  Save,
  CheckCircle,
  Science,
  VerifiedUser,
  Print,
  ArrowBack,
  Calculate,
  Warning,
  Refresh,
  Edit,
  CalendarToday,
  Delete,
  Block,
  MoreVert,
  History,
  AccessTime,
  Event,
  Schedule,
  Today,
  DateRange,
  WatchLater,
  Done,
  Error,
  Person,
  LocalHospital,
  MedicalServices,
  Engineering,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { resultEntryService } from '../../../../../services/resultEntryService';
import { ReferenceRangeCalculator } from '../../../../../utils/ReferenceRangeCalculator';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

export const ResultEntry = () => {
  const { id: resultId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Main states
  const [activeStep, setActiveStep] = useState(0);
  const [resultData, setResultData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [verificationDialog, setVerificationDialog] = useState(false);
  const [saveDialog, setSaveDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [technician, setTechnician] = useState('');
  const [instrument, setInstrument] = useState('');
  const [method, setMethod] = useState('');
  const [inputValues, setInputValues] = useState({});
  const [calculatedResults, setCalculatedResults] = useState({});

  // Date editing states - Service-specific
  const [dateEditDialog, setDateEditDialog] = useState(false);
  const [editDates, setEditDates] = useState({
    collectionDate: null,
    receivedDate: null,
    testedAt: null,
    verifiedAt: null
  });

  // Delete/Cancel states
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [restoreDialog, setRestoreDialog] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [deleteReason, setDeleteReason] = useState('');

  // History states
  const [dateHistoryDialog, setDateHistoryDialog] = useState(false);
  const [cancellationHistoryDialog, setCancellationHistoryDialog] = useState(false);
  const [dateHistory, setDateHistory] = useState([]);
  const [cancellationHistory, setCancellationHistory] = useState([]);

  // Technician states
  const [loggedInTechnician, setLoggedInTechnician] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [doctorSelectionDialog, setDoctorSelectionDialog] = useState(false);
  const [verificationType, setVerificationType] = useState('');

  const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
  const steps = ['Enter Results', 'Verify', 'Release'];

  const editorConfiguration = {
    toolbar: {
      items: [
        'heading', '|', 'bold', 'italic', 'underline', 'strikethrough', '|',
        'bulletedList', 'numberedList', '|', 'outdent', 'indent', '|',
        'insertTable', '|', 'undo', 'redo'
      ]
    }
  };

  // Universal safety helper for rendering values
  const safeRender = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'object') {
      try {
        if (Object.keys(val).length > 0) {
          // If it's the grid object, show its main keys (rows/columns)
          return Object.keys(val).join(', ');
        }
        return JSON.stringify(val);
      } catch (e) {
        return '[Complex Data]';
      }
    }
    return val;
  };

  useEffect(() => {
    loadResultEntry();
    loadLoggedInTechnician();
  }, [resultId]);

  const loadLoggedInTechnician = async () => {
    try {
      if (!user) return;
      const response = await resultEntryService.getTechnicianById(user._id || user.id);
      if (response.success && response.data) {
        setLoggedInTechnician(response.data);
        setTechnician(response.data._id);
        if (response.data.doctors && response.data.doctors.length === 1) {
          setSelectedDoctor(response.data.doctors[0]);
        }
      }
    } catch (error) {
      console.error('Error loading technician info:', error);
    }
  };

  const loadResultEntry = async () => {
    setLoading(true);
    try {
      const response = await resultEntryService.getResultById(resultId);
      if (response.success) {
        setResultData(response.data);
        
        const { serviceId } = location.state || {};
        if (serviceId && response.data.service_results) {
          const service = response.data.service_results.find(
            sr => sr.service?._id === serviceId || sr.service === serviceId
          );
          setSelectedService(service || response.data.service_results[0]);
        } else if (response.data.service_results?.length > 0) {
          setSelectedService(response.data.service_results[0]);
        }

        initializeInputValues(response.data);
        updateActiveStep(response.data);
        showSnackbar('Result entry loaded successfully', 'success');
      } else {
        showSnackbar(response.message || 'Failed to load result entry', 'error');
      }
    } catch (error) {
      console.error('Error loading result entry:', error);
      showSnackbar(error.response?.data?.message || 'Failed to load result entry', 'error');
    } finally {
      setLoading(false);
    }
  };

  const initializeInputValues = (data) => {
    if (!data.service_results) return;
    const initialValues = {};

    const updatedServiceResults = data.service_results.map(service => {
      const updatedResults = service.results?.map(result => {
        initialValues[result._id] = {
          displayValue: result.value || '',
          resultData: result
        };

        if (!result.referenceRange && result.parameter) {
          const param = service.service?.parameters?.find(p => p._id === result.parameter);
          if (param) {
            const referenceRangeInfo = getReferenceRangeForParameter(param, result);
            const valueToCheck = result.value || initialValues[result._id]?.displayValue;
            let status = 'normal';

            if (valueToCheck && !isNaN(valueToCheck)) {
              status = getResultStatusWithReference(valueToCheck, param);
            }

            return {
              ...result,
              referenceRange: referenceRangeInfo.displayText,
              referenceRangeText: referenceRangeInfo.displayText,
              referenceRangeMatched: referenceRangeInfo.matchType,
              appliedReferenceRange: referenceRangeInfo.matchedRange,
              status: result.status || status
            };
          }
        }
        return result;
      });

      return {
        ...service,
        results: updatedResults
      };
    });

    setInputValues(initialValues);
    setResultData(prevData => ({
      ...prevData,
      service_results: updatedServiceResults
    }));

    if (selectedService) {
      const updatedSelectedService = updatedServiceResults.find(
        s => s._id === selectedService._id
      );
      if (updatedSelectedService) {
        setSelectedService(updatedSelectedService);
      }
    }
  };

  const calculateDerivedResults = async () => {
    try {
      const response = await resultEntryService.calculateDerivedResults(
        resultData._id,
        selectedService._id
      );
      if (response.success) {
        initializeInputValues(response.data);
        setResultData(response.data);
        const updatedService = response.data.service_results.find(
          s => s._id === selectedService._id
        );
        setSelectedService(updatedService);
        showSnackbar('Calculated parameters updated', 'info');
      }
    } catch (error) {
      console.error('Error calculating derived results:', error);
      showSnackbar('Error calculating derived parameters', 'error');
    }
  };

  const handleResultChange = (resultId, value) => {
    if (!selectedService) return;
    const updatedInputValues = {
      ...inputValues,
      [resultId]: {
        ...inputValues[resultId],
        displayValue: value
      }
    };
    setInputValues(updatedInputValues);

    const changedResult = selectedService.results.find(r => r._id === resultId);
    if (!changedResult) return;
    const param = selectedService.service?.parameters?.find(
      p => p._id === changedResult.parameter
    );

    let newStatus = 'normal';
    let numericValue = null;
    let referenceRange = '';
    let referenceRangeText = '';
    let referenceRangeMatched = 'text';
    let appliedReferenceRange = null;

    const isNumericValue = value !== '' && value !== null && typeof value !== 'object' && !isNaN(value);
    
    if (isNumericValue) {
      numericValue = parseFloat(value);
      const referenceRangeInfo = getReferenceRangeForParameter(param, changedResult);
      newStatus = getResultStatusWithReference(value, param);
      referenceRange = referenceRangeInfo.displayText;
      referenceRangeText = referenceRangeInfo.displayText;
      referenceRangeMatched = referenceRangeInfo.matchType;
      appliedReferenceRange = referenceRangeInfo.matchedRange;
    } else if (param) {
      const referenceRangeInfo = getReferenceRangeForParameter(param, changedResult);
      referenceRange = referenceRangeInfo.displayText;
      referenceRangeText = referenceRangeInfo.displayText;
      referenceRangeMatched = referenceRangeInfo.matchType;
      appliedReferenceRange = referenceRangeInfo.matchedRange;
    }

    const updatedResults = selectedService.results.map(result =>
      result._id === resultId
        ? {
          ...result,
          value,
          numericValue,
          status: newStatus,
          referenceRange,
          referenceRangeText,
          referenceRangeMatched,
          appliedReferenceRange
        }
        : result
    );

    const updatedService = { ...selectedService, results: updatedResults };
    setSelectedService(updatedService);
    setResultData(prevData => ({
      ...prevData,
      service_results: prevData.service_results.map(service =>
        service._id === selectedService._id ? updatedService : service
      )
    }));
  };

  const handleGridResultChange = (resultId, row, col, val) => {
    const currentVal = inputValues[resultId]?.displayValue;
    // Ensure currentVal is an object, but preserve any existing string as the summary
    let gridData = typeof currentVal === 'object' ? { ...currentVal } : { _summary: currentVal || '' };
    
    gridData[row] = {
      ...(gridData[row] || {}),
      [col]: val
    };
    
    handleResultChange(resultId, gridData);
  };

  const saveResults = async () => {
    if (!selectedService || !resultData) return;
    setSaving(true);
    try {
      const resultsToSave = selectedService.results.map(result => {
        const inputValue = inputValues[result._id]?.displayValue;
        const calculatedResult = calculatedResults[result._id];

        return {
          _id: result._id,
          parameter: result.parameter,
          value: calculatedResult ? calculatedResult.value : (inputValue || result.value),
          numericValue: calculatedResult ? calculatedResult.numericValue :
            (inputValue && !isNaN(inputValue) ? parseFloat(inputValue) : result.numericValue),
          unit: calculatedResult ? calculatedResult.unit : result.unit,
          status: result.status,
          remarks: result.remarks,
          referenceRange: result.referenceRange || "",
          referenceRangeText: result.referenceRangeText || "",
          referenceRangeMatched: result.referenceRangeMatched || "text",
          appliedReferenceRange: result.appliedReferenceRange || null
        };
      });

      const updateData = {
        results: resultsToSave,
        status: 'testing',
        technician,
        instrument,
        method
      };

      const response = await resultEntryService.updateTestResults(
        resultData._id,
        selectedService._id,
        updateData
      );

      if (response.success) {
        setResultData(response.data);
        const updatedService = response.data.service_results.find(
          s => s._id === selectedService._id
        );
        setSelectedService(updatedService);
        initializeInputValues(response.data);
        showSnackbar('Results saved successfully', 'success');
        setSaveDialog(false);
      } else {
        showSnackbar(response.message || 'Failed to save results', 'error');
      }
    } catch (error) {
      console.error('Error saving results:', error);
      showSnackbar(error.response?.data?.message || 'Failed to save results', 'error');
    } finally {
      setSaving(false);
    }
  };

  const markAsCompleted = async () => {
    setSaving(true);
    try {
      const resultsWithReferenceRanges = selectedService.results.map(result => {
        const inputValue = inputValues[result._id]?.displayValue;
        const calculatedResult = calculatedResults[result._id];
        const currentValue = calculatedResult ? calculatedResult.value : (inputValue || result.value);
        const param = selectedService.service?.parameters?.find(
          p => p._id === result.parameter
        );

        let referenceRangeInfo = {};
        let status = result.status;

        if (param && currentValue) {
          referenceRangeInfo = getReferenceRangeForParameter(param, result);
          if (!isNaN(currentValue)) {
            status = getResultStatusWithReference(currentValue, param);
          }
        }

        return {
          _id: result._id,
          parameter: result.parameter,
          value: currentValue,
          numericValue: calculatedResult ? calculatedResult.numericValue :
            (inputValue && !isNaN(inputValue) ? parseFloat(inputValue) : result.numericValue),
          unit: calculatedResult ? calculatedResult.unit : result.unit,
          status: status,
          remarks: result.remarks,
          referenceRange: referenceRangeInfo.displayText || result.referenceRange || "",
          referenceRangeText: referenceRangeInfo.displayText || result.referenceRangeText || "",
          referenceRangeMatched: referenceRangeInfo.matchType || result.referenceRangeMatched || "text",
          appliedReferenceRange: referenceRangeInfo.matchedRange || result.appliedReferenceRange || null
        };
      });

      const updateData = {
        results: resultsWithReferenceRanges,
        status: 'completed',
        technician,
        instrument,
        method,
        testedAt: new Date().toISOString()
      };

      const response = await resultEntryService.updateTestResults(
        resultData._id,
        selectedService._id,
        updateData
      );

      if (response.success) {
        await calculateDerivedResults();
        setResultData(response.data);
        const updatedService = response.data.service_results.find(
          s => s._id === selectedService._id
        );
        setSelectedService(updatedService);
        setActiveStep(1);
        showSnackbar('Results marked as completed', 'success');
        setVerificationDialog(false);
      } else {
        showSnackbar(response.message || 'Failed to complete results', 'error');
      }
    } catch (error) {
      console.error('Error completing results:', error);
      showSnackbar(error.response?.data?.message || 'Failed to complete results', 'error');
    } finally {
      setSaving(false);
    }
  };

  const verifyResults = async () => {
    if (loggedInTechnician?.doctors && loggedInTechnician.doctors.length > 0 && !selectedDoctor) {
      setVerificationType('verify');
      setDoctorSelectionDialog(true);
      return;
    }

    setSaving(true);
    try {
      const verificationData = {
        verifiedBy: technician || loggedInTechnician?._id,
        verifiedAt: new Date().toISOString(),
        verifiedForDoctor: selectedDoctor?._id,
      };
   console.log(verificationData)
      const response = await resultEntryService.verifyResults(
        resultData._id,
        selectedService._id,
        verificationData
      );

      if (response.success) {
        setResultData(response.data);
        const updatedService = response.data.service_results.find(
          s => s._id === selectedService._id
        );
        setSelectedService(updatedService);
        setActiveStep(2);
        const doctorName = selectedDoctor?.doctor || 'Unknown Doctor';
        showSnackbar(`Results verified successfully for Dr. ${doctorName}`, 'success');
      } else {
        showSnackbar(response.message || 'Failed to verify results', 'error');
      }
    } catch (error) {
      console.error('Error verifying results:', error);
      showSnackbar(error.response?.data?.message || 'Failed to verify results', 'error');
    } finally {
      setSaving(false);
    }
  };

  const releaseResults = async () => {
    setSaving(true);
    try {
      const releaseData = {
        releasedBy: user?.SuperAdminId || user?.id,
        releasedAt: new Date().toISOString()
      };

      const response = await resultEntryService.releaseResults(
        resultData._id,
        releaseData
      );

      if (response.success) {
        setResultData(response.data);
        showSnackbar('Results released successfully', 'success');
        setTimeout(() => {
          navigate('/hospital/laboratory/result-dashboard');
        }, 2000);
      } else {
        showSnackbar(response.message || 'Failed to release results', 'error');
      }
    } catch (error) {
      console.error('Error releasing results:', error);
      showSnackbar(error.response?.data?.message || 'Failed to release results', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ==================== DATE EDITING FUNCTIONS ====================
  const handleDateEdit = () => {
    if (!selectedService) {
      showSnackbar('Please select a service first', 'warning');
      return;
    }

    setEditDates({
      collectionDate: selectedService?.collectionDate ? dayjs(selectedService.collectionDate) : null,
      receivedDate: selectedService?.receivedDate ? dayjs(selectedService.receivedDate) : null,
      testedAt: selectedService?.testedAt ? dayjs(selectedService.testedAt) : null,
      verifiedAt: selectedService?.verifiedAt ? dayjs(selectedService.verifiedAt) : null
    });
    setDateEditDialog(true);
  };

  const saveEditedDates = async () => {
    if (!selectedService || !resultData) {
      showSnackbar('No service selected', 'error');
      return;
    }

    setSaving(true);
    try {
      const serviceDateUpdates = {};
      
      if (editDates.collectionDate) {
        serviceDateUpdates.collectionDate = editDates.collectionDate.toISOString();
      }
      if (editDates.receivedDate) {
        serviceDateUpdates.receivedDate = editDates.receivedDate.toISOString();
      }
      if (editDates.testedAt) {
        serviceDateUpdates.testedAt = editDates.testedAt.toISOString();
      }
      if (editDates.verifiedAt) {
        serviceDateUpdates.verifiedAt = editDates.verifiedAt.toISOString();
      }

      if (Object.keys(serviceDateUpdates).length > 0) {
        await resultEntryService.updateServiceDates(
          resultData._id,
          selectedService._id,
          serviceDateUpdates
        );
      }

      await loadResultEntry();
      setDateEditDialog(false);
      showSnackbar('Service dates updated successfully', 'success');
    } catch (error) {
      console.error('Error updating dates:', error);
      showSnackbar(error.response?.data?.message || 'Failed to update dates', 'error');
    } finally {
      setSaving(false);
    }
  };

  const loadDateHistory = async () => {
    try {
      const response = await resultEntryService.getDateEditHistory(resultData._id);
      if (response.success) {
        setDateHistory(response.data?.dateEditHistory || []);
        setDateHistoryDialog(true);
      }
    } catch (error) {
      console.error('Error loading date history:', error);
      showSnackbar('Failed to load date history', 'error');
    }
  };

  // ==================== DELETE/CANCEL FUNCTIONS ====================
  const handleActionMenuOpen = (event) => {
    setActionMenuAnchor(event.currentTarget);
    setActionMenuOpen(true);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setActionMenuOpen(false);
  };

  const cancelTestReport = async () => {
    setSaving(true);
    try {
      const cancellationData = {
        cancelledBy: user?.SuperAdminId || user?.id,
        cancellationReason: cancellationReason || 'Cancelled by user',
        cancelledAt: new Date().toISOString()
      };

      const response = await resultEntryService.cancelTestReport(
        resultData._id,
        cancellationData
      );

      if (response.success) {
        setResultData(response.data);
        showSnackbar('Test report cancelled successfully', 'success');
        setCancelDialog(false);
        setCancellationReason('');
        setTimeout(() => {
          navigate('/hospital/laboratory/result-dashboard');
        }, 2000);
      } else {
        showSnackbar(response.message || 'Failed to cancel test report', 'error');
      }
    } catch (error) {
      console.error('Error cancelling test report:', error);
      showSnackbar(error.response?.data?.message || 'Failed to cancel test report', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteTestReport = async () => {
    setSaving(true);
    try {
      const deleteData = {
        deletedBy: user?.SuperAdminId || user?.id,
        deleteReason: deleteReason || 'Deleted by user',
        deletedAt: new Date().toISOString()
      };

      const response = await resultEntryService.softDeleteTestReport(
        resultData._id,
        deleteData
      );

      if (response.success) {
        setResultData(response.data);
        showSnackbar('Test report deleted successfully', 'success');
        setDeleteDialog(false);
        setDeleteReason('');
        setTimeout(() => {
          navigate('/hospital/laboratory/result-dashboard');
        }, 2000);
      } else {
        showSnackbar(response.message || 'Failed to delete test report', 'error');
      }
    } catch (error) {
      console.error('Error deleting test report:', error);
      showSnackbar(error.response?.data?.message || 'Failed to delete test report', 'error');
    } finally {
      setSaving(false);
    }
  };

  const restoreTestReport = async () => {
    setSaving(true);
    try {
      const restoreData = {
        restoredBy: user?.SuperAdminId || user?.id,
        restoredAt: new Date().toISOString()
      };

      let response;
      if (resultData.deleted) {
        response = await resultEntryService.restoreDeletedTestReport(
          resultData._id,
          restoreData
        );
      } else if (resultData.cancelled) {
        response = await resultEntryService.restoreTestReport(
          resultData._id,
          restoreData
        );
      }

      if (response?.success) {
        setResultData(response.data);
        showSnackbar('Test report restored successfully', 'success');
        setRestoreDialog(false);
        await loadResultEntry();
      } else {
        showSnackbar(response?.message || 'Failed to restore test report', 'error');
      }
    } catch (error) {
      console.error('Error restoring test report:', error);
      showSnackbar(error.response?.data?.message || 'Failed to restore test report', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ==================== HELPER FUNCTIONS ====================
  const updateActiveStep = (data) => {
    if (!data.service_results) return;
    const selectedServiceData = selectedService || data.service_results[0];
    const status = selectedServiceData?.status;

    switch (status) {
      case 'pending':
      case 'testing':
        setActiveStep(0);
        break;
      case 'completed':
        setActiveStep(1);
        break;
      case 'verified':
      case 'released':
        setActiveStep(2);
        break;
      default:
        setActiveStep(0);
    }
  };

  const getResultStatusWithReference = (value, parameter) => {
    if (!parameter || value === null || value === undefined || value === '') {
      return 'normal';
    }
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return 'normal';

    if (!parameter.referenceRanges || parameter.referenceRanges.length === 0) {
      return 'normal';
    }

    const patientAge = resultData?.age;
    const patientGender = resultData?.gender;
    const patientAgeInDays = resultData?.age_in_days;

    if (!patientAge || !patientGender) {
      return 'normal';
    }

    const referenceRange = ReferenceRangeCalculator.findBestReferenceRange(
      parameter,
      patientAge,
      patientGender,
      patientAgeInDays
    );

    return ReferenceRangeCalculator.getResultStatus(numericValue, referenceRange);
  };

  const getReferenceRangeForParameter = (parameter, result) => {
    if (!parameter) return { displayText: 'N/A', matchType: 'text', matched: false };

    if (!parameter.referenceRanges || parameter.referenceRanges.length === 0) {
      const refText = parameter.referenceRangeText;
      let displayText = 'N/A';
      if (refText) {
        if (typeof refText === 'object') {
          try {
            displayText = Object.values(refText).join(', ');
          } catch (e) {
            displayText = 'N/A';
          }
        } else {
          displayText = refText;
        }
      }
      return {
        displayText: displayText,
        matchType: 'text',
        matched: false
      };
    }

    const patientAge = resultData?.age;
    const patientGender = resultData?.gender;
    const patientAgeInDays = resultData?.age_in_days;

    if (!patientAge || !patientGender) {
      const refText = parameter.referenceRangeText;
      let displayText = 'N/A';
      if (refText) {
        if (typeof refText === 'object') {
          try {
            displayText = Object.values(refText).join(', ');
          } catch (e) {
            displayText = 'N/A';
          }
        } else {
          displayText = refText;
        }
      }
      return {
        displayText: displayText,
        matchType: 'text',
        matched: false
      };
    }

    const rangeResult = ReferenceRangeCalculator.findBestReferenceRange(
      parameter,
      patientAge,
      patientGender,
      patientAgeInDays
    );

    if (rangeResult.displayText && typeof rangeResult.displayText === 'object') {
      try {
        rangeResult.displayText = Object.values(rangeResult.displayText).join(', ');
      } catch (e) {
        rangeResult.displayText = 'N/A';
      }
    }

    return rangeResult;
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { color: 'warning', label: 'Pending', icon: <AccessTime /> },
      testing: { color: 'info', label: 'Testing', icon: <Science /> },
      completed: { color: 'success', label: 'Completed', icon: <CheckCircle /> },
      verified: { color: 'primary', label: 'Verified', icon: <VerifiedUser /> },
      released: { color: 'secondary', label: 'Released', icon: <Print /> },
      cancelled: { color: 'error', label: 'Cancelled', icon: <Block /> },
      deleted: { color: 'error', label: 'Deleted', icon: <Delete /> }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        variant="filled"
        icon={config.icon}
      />
    );
  };

  const getResultStatusChip = (status) => {
    const statusConfig = {
      normal: { color: 'success', label: 'Normal', icon: <Done /> },
      low: { color: 'warning', label: 'Low', icon: <Warning /> },
      high: { color: 'error', label: 'High', icon: <Warning /> },
      critical_low: { color: 'error', label: 'Critical Low', icon: <Error /> },
      critical_high: { color: 'error', label: 'Critical High', icon: <Error /> },
      abnormal: { color: 'warning', label: 'Abnormal', icon: <Warning /> }
    };

    const config = statusConfig[status] || statusConfig.normal;
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
      />
    );
  };

  const renderReferenceRangeCell = (parameter, result) => {
    const referenceRangeInfo = getReferenceRangeForParameter(parameter, result);
    // Safety check: ensure displayText is not an object
    const displayRangeText = typeof referenceRangeInfo.displayText === 'object' ? 
      'N/A' : (referenceRangeInfo.displayText || 'N/A');
    
    const matchBadge = ReferenceRangeCalculator.getMatchBadge(referenceRangeInfo.matchType);

    return (
      <>
        <Typography variant="body2" color="textSecondary">
          {displayRangeText}
        </Typography>
        {referenceRangeInfo.matched && parameter.referenceRanges?.length > 0 && (
          <Tooltip title={`${matchBadge?.tooltip || ''} | Age: ${resultData?.age}, Gender: ${resultData?.gender}`}>
            <Chip
              label={matchBadge?.label || 'Match'}
              size="small"
              color={matchBadge?.color || 'default'}
              variant="outlined"
              sx={{ mt: 0.5, fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>
        )}
      </>
    );
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canEditDates = () => {
    if (!resultData) return false;
    if (location.state?.mode === 'edit') return true;
    return !(resultData.overall_status === 'released' ||
      resultData.cancelled ||
      resultData.deleted);
  };

  const canCancelOrDelete = () => {
    if (!resultData) return false;
    return !(resultData.overall_status === 'released' ||
      resultData.cancelled ||
      resultData.deleted);
  };

  const getServiceTimeline = (service) => {
    return [
      { label: 'Collection', date: service.collectionDate, icon: <Event fontSize="small" color="primary" /> },
      { label: 'Received', date: service.receivedDate, icon: <Schedule fontSize="small" color="primary" /> },
      { label: 'Tested', date: service.testedAt, icon: <Science fontSize="small" color="primary" /> },
      { label: 'Verified', date: service.verifiedAt, icon: <VerifiedUser fontSize="small" color="primary" /> }
    ];
  };

  const getTechnicianName = (technician) => {
    if (!technician) return 'Lab Technician';
    if (typeof technician === 'string') return technician;
    if (technician.name) return technician.name;
    if (technician.first_name && technician.last_name) {
      return `${technician.first_name} ${technician.last_name}`;
    }
    if (technician.first_name) return technician.first_name;
    if (technician.username) return technician.username;
    return 'Lab Technician';
  };

  const renderTechnicianInfoCard = () => (
    <Card elevation={2} sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Engineering color="primary" />
          Test Information
        </Typography>

        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'primary.50', borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                  {loggedInTechnician?.name?.charAt(0) || 'T'}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {loggedInTechnician?.name || 'Logged-in Technician'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {loggedInTechnician?.employeeId || 'ID: N/A'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={9}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {loggedInTechnician?.authorizedSections?.map(section => (
                  <Chip key={section} label={section} size="small" color="primary" variant="outlined" />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {loggedInTechnician?.doctors && loggedInTechnician.doctors.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Select Doctor for This Test
            </Typography>
            {selectedDoctor ? (
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'success.50', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedDoctor.doctor}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {selectedDoctor.heading_1 || 'No designation'}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setDoctorSelectionDialog(true)}
                    disabled={!canEditDates()}
                  >
                    Change
                  </Button>
                </Box>
              </Paper>
            ) : (
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setDoctorSelectionDialog(true)}
                disabled={!canEditDates()}
                sx={{ py: 1.5 }}
              >
                Select Doctor for This Test
              </Button>
            )}
          </Box>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Instrument"
              value={instrument}
              onChange={(e) => setInstrument(e.target.value)}
              disabled={!canEditDates()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MedicalServices />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Method"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              disabled={!canEditDates()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Science />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderDoctorSelectionDialog = () => (
    <Dialog
      open={doctorSelectionDialog}
      onClose={() => setDoctorSelectionDialog(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BadgeIcon color="primary" />
          Select Doctor for {verificationType === 'verify' ? 'Verification' : 'Result Entry'}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Please select which doctor you are performing this action for:
        </Typography>
        <Box sx={{ mt: 2 }}>
          {loggedInTechnician?.doctors?.map((doctor) => (
            <Paper
              key={doctor._id}
              elevation={selectedDoctor?._id === doctor._id ? 3 : 1}
              sx={{
                p: 2,
                mb: 1,
                cursor: 'pointer',
                border: selectedDoctor?._id === doctor._id
                  ? '2px solid #1976d2'
                  : '1px solid #e0e0e0',
                backgroundColor: selectedDoctor?._id === doctor._id
                  ? 'primary.50'
                  : 'background.paper',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
              onClick={() => {
                setSelectedDoctor(doctor);
                setDoctorSelectionDialog(false);
                if (verificationType === 'verify') {
                  verifyResults();
                } else if (verificationType === 'save') {
                  saveResults();
                }
              }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight="medium">
                  {doctor.doctor}
                </Typography>
                {doctor.heading_1 && (
                  <Typography variant="body2" color="textSecondary">
                    {doctor.heading_1}
                  </Typography>
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDoctorSelectionDialog(false)}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" align="center" sx={{ mt: 2 }}>
          Loading result entry...
        </Typography>
      </Box>
    );
  }

  if (!resultData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load result data</Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        {/* Header and patient info */}
        <Card elevation={3} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h4" gutterBottom color="primary">
                  Result Entry
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  Enter and verify laboratory test results
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBack />}
                  onClick={() => navigate(-1)}
                >
                  Back to Dashboard
                </Button>
                <IconButton
                  color="primary"
                  onClick={handleActionMenuOpen}
                  disabled={!canCancelOrDelete()}
                >
                  <MoreVert />
                </IconButton>
              </Box>
            </Box>

            {/* Patient Info */}
            <Paper elevation={1} sx={{ p: 2, mb: 2, backgroundColor: 'grey.50', position: 'relative' }}>
              {selectedService && canEditDates() && (
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  <Tooltip title="Edit Service Dates">
                    <IconButton size="small" onClick={handleDateEdit}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              <Box sx={{ position: 'absolute', top: 8, right: selectedService && canEditDates() ? 40 : 8 }}>
                <Tooltip title="View Date History">
                  <IconButton size="small" onClick={loadDateHistory}>
                    <History fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" color="textSecondary">Patient Name</Typography>
                  <Typography variant="body1" fontWeight="medium">{resultData.patient_name}</Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant="subtitle2" color="textSecondary">UHID</Typography>
                  <Typography variant="body1">{safeRender(resultData.uhid)}</Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant="subtitle2" color="textSecondary">Barcode</Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">{resultData.barcode_no}</Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant="subtitle2" color="textSecondary">Age/Gender</Typography>
                  <Typography variant="body1">{safeRender(resultData.age)} • {safeRender(resultData.gender)}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" color="textSecondary">Referring Doctor</Typography>
                  <Typography variant="body1">{safeRender(resultData.doctor?.name)}</Typography>
                </Grid>

                {/* Service-specific Date Timeline */}
                {selectedService && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                      {selectedService.service?.test_name} Timeline
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      {getServiceTimeline(selectedService).map((item, index) => (
                        <Grid item xs={6} md={3} key={index}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {item.icon}
                            <Box>
                              <Typography variant="caption" color="textSecondary">{safeRender(item.label)}</Typography>
                              <Typography variant="body2">
                                {item.date ? formatDate(item.date) : 'Not set'}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Status and Progress Stepper */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle1">Overall Status:</Typography>
                {getStatusChip(resultData.overall_status)}
                {resultData.cancelled && (
                  <Tooltip title={resultData.cancellationReason}>
                    <Chip
                      label={`Cancelled: ${resultData.cancellationReason?.substring(0, 30)}...`}
                      color="error"
                      size="small"
                      variant="outlined"
                      icon={<Block />}
                    />
                  </Tooltip>
                )}
                {resultData.deleted && (
                  <Tooltip title={resultData.deleteReason}>
                    <Chip
                      label={`Deleted: ${resultData.deleteReason?.substring(0, 30)}...`}
                      color="error"
                      size="small"
                      variant="outlined"
                      icon={<Delete />}
                    />
                  </Tooltip>
                )}
              </Box>

              {!resultData.cancelled && !resultData.deleted && (
                <Stepper activeStep={activeStep} sx={{ flex: 1, maxWidth: 600 }}>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Main Content Grid */}
        <Grid container spacing={3}>
          {/* Left Sidebar */}
          <Grid item xs={12} md={3}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Available Tests</Typography>
                  <Tooltip title="Refresh">
                    <IconButton size="small" onClick={loadResultEntry}>
                      <Refresh fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                  {resultData.service_results?.map((service) => (
                    <Paper
                      key={service._id}
                      elevation={selectedService?._id === service._id ? 3 : 1}
                      sx={{
                        p: 2,
                        mb: 1,
                        cursor: 'pointer',
                        backgroundColor: selectedService?._id === service._id
                          ? 'primary.light'
                          : service.cancelled
                            ? 'error.50'
                            : service.deleted
                              ? 'grey.100'
                              : 'background.paper',
                        color: selectedService?._id === service._id ? 'white' : 'text.primary',
                        opacity: service.cancelled || service.deleted ? 0.7 : 1,
                        borderLeft: service.cancelled ? '4px solid #f44336' :
                          service.deleted ? '4px solid #757575' : '4px solid transparent',
                        '&:hover': {
                          backgroundColor: selectedService?._id === service._id
                            ? 'primary.main'
                            : service.cancelled
                              ? 'error.100'
                              : service.deleted
                                ? 'grey.200'
                                : 'grey.100'
                        }
                      }}
                      onClick={() => {
                        if (!service.cancelled && !service.deleted) {
                          setSelectedService(service);
                          updateActiveStep(resultData);
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {service.service?.test_name || 'Unknown Test'}
                          </Typography>
                          {service.cancelled && (
                            <Chip label="Cancelled" size="small" color="error" sx={{ mt: 0.5 }} />
                          )}
                          {service.deleted && (
                            <Chip label="Deleted" size="small" color="default" sx={{ mt: 0.5 }} />
                          )}
                        </Box>
                        {getStatusChip(service.status)}
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </CardContent>
            </Card>

            {renderTechnicianInfoCard()}

            {/* Action Buttons */}
            <Card elevation={2} sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Actions</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {/* Always show Save Progress if activeStep is 0 OR if we are amending (mode === 'edit') */}
                  {(activeStep === 0 || location.state?.mode === 'edit') && !resultData.cancelled && !resultData.deleted && (
                    <>
                      <Button
                        variant="contained"
                        startIcon={<Save />}
                        onClick={() => setSaveDialog(true)}
                        disabled={saving || !canEditDates()}
                        fullWidth
                      >
                        {saving ? 'Saving...' : (location.state?.mode === 'edit' && activeStep !== 0 ? 'Save Amended Results' : 'Save Progress')}
                      </Button>
                      {activeStep === 0 && (
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={() => {
                          if (loggedInTechnician?.doctors && loggedInTechnician.doctors.length > 0 && !selectedDoctor) {
                            setVerificationType('verify');
                            setDoctorSelectionDialog(true);
                          } else {
                            markAsCompleted();
                          }
                        }}
                        disabled={!selectedService || saving || !canEditDates()}
                        fullWidth
                      >
                        Mark Complete
                      </Button>
                      )}
                    </>
                  )}
                  {activeStep === 1 && !resultData.cancelled && !resultData.deleted && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<VerifiedUser />}
                      onClick={verifyResults}
                      disabled={saving || !canEditDates()}
                      fullWidth
                    >
                      {saving ? 'Verifying...' : 'Verify Results'}
                    </Button>
                  )}
                  {activeStep === 2 && !resultData.cancelled && !resultData.deleted && (
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<Print />}
                      onClick={releaseResults}
                      disabled={saving || !canEditDates()}
                      fullWidth
                    >
                      {saving ? 'Releasing...' : 'Release Results'}
                    </Button>
                  )}
                  {(resultData.cancelled || resultData.deleted) && (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<Refresh />}
                      onClick={() => setRestoreDialog(true)}
                      disabled={saving}
                      fullWidth
                    >
                      Restore Test Report
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Main Content */}
          <Grid item xs={12} md={9}>
            {selectedService && !selectedService.cancelled && !selectedService.deleted ? (
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5">
                      {selectedService.service?.test_name || 'Unknown Test'}
                    </Typography>
                    <Tooltip title="Recalculate derived parameters">
                      <IconButton
                        color="primary"
                        onClick={calculateDerivedResults}
                        disabled={selectedService.status === 'released' || !canEditDates()}
                      >
                        <Refresh />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  { (activeStep === 0 || location.state?.mode === 'edit') ? (
                    <TableContainer component={Paper} elevation={1}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: 'primary.main' }}>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '25%' }}>Parameter</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '20%' }}>Result</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '10%' }}>Unit</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '20%' }}>Reference Range</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '15%' }}>Status</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '10%' }}>Type</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedService.results?.map((result) => {
                            // Support both populated and unpopulated parameter references
                            const paramId = result.parameter?._id || result.parameter;
                            const parameter = selectedService.service?.parameters?.find(
                              p => p._id === paramId
                            );
                            
                            const isCalculated = parameter?.isCalculated;
                            const calculatedValue = calculatedResults[result._id];
                            
                            // Sanitize raw value: if it's an object but not a grid, stringify it
                            let rawVal = isCalculated && calculatedValue ?
                              calculatedValue.value :
                              (inputValues[result._id]?.displayValue || result.value || '');
                            
                            const displayValue = (typeof rawVal === 'object' && !parameter?.isGrid) ?
                              JSON.stringify(rawVal) : rawVal;

                            return (
                              <TableRow key={result._id} hover>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="medium">
                                    {(() => {
                                      const label = parameter?.label;
                                      if (!label) return 'Unknown Parameter';
                                      if (typeof label === 'object') {
                                        try {
                                          return Object.values(label).join(', ') || 'Unknown Parameter';
                                        } catch (e) {
                                          return 'Unknown Parameter';
                                        }
                                      }
                                      return label;
                                    })()}
                                  </Typography>
                                  {parameter?.helpDescriptions && (
                                    <Typography variant="caption" color="textSecondary">
                                      {typeof parameter.helpDescriptions === 'object' 
                                        ? JSON.stringify(parameter.helpDescriptions) 
                                        : parameter.helpDescriptions}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {/* 1. Grid table — shown FIRST when isGrid is true */}
                                  {parameter?.isGrid && parameter.gridConfig?.rows?.length > 0 && (
                                    <Box sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                                            <TableCell sx={{ fontWeight: 'bold', py: 0.5, fontSize: '0.75rem' }}>Label</TableCell>
                                            {parameter.gridConfig.columns?.map((col, i) => (
                                              <TableCell key={i} align="center" sx={{ fontWeight: 'bold', py: 0.5, fontSize: '0.75rem' }}>{col}</TableCell>
                                            ))}
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {parameter.gridConfig.rows.map((row, i) => (
                                            <TableRow key={i}>
                                              <TableCell sx={{ py: 0.5, fontWeight: 'medium', fontSize: '0.75rem' }}>{row}</TableCell>
                                              {parameter.gridConfig.columns.map((col, j) => (
                                                <TableCell key={j} align="center" sx={{ py: 0.5 }}>
                                                  {parameter.gridConfig.options?.length > 0 ? (
                                                    <Select
                                                      size="small"
                                                      value={safeRender(typeof displayValue === 'object' ? displayValue?.[row]?.[col] : '') || ''}
                                                      onChange={(e) => handleGridResultChange(result._id, row, col, e.target.value)}
                                                      disabled={!canEditDates()}
                                                      sx={{ minWidth: 70, height: 28, fontSize: '0.75rem' }}
                                                    >
                                                      <MenuItem value="">-</MenuItem>
                                                      {parameter.gridConfig.options.map((opt, k) => (
                                                        <MenuItem key={k} value={opt}>{opt}</MenuItem>
                                                      ))}
                                                    </Select>
                                                  ) : (
                                                    <TextField
                                                      size="small"
                                                      value={safeRender(typeof displayValue === 'object' ? displayValue?.[row]?.[col] : '') || ''}
                                                      onChange={(e) => handleGridResultChange(result._id, row, col, e.target.value)}
                                                      disabled={!canEditDates()}
                                                      placeholder="-"
                                                      sx={{ width: 80, '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5, px: 1 } }}
                                                    />
                                                  )}
                                                </TableCell>
                                              ))}
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  )}

                                  {/* 2. Text Input or CKEditor */}
                                  {parameter?.inputType === 'paragraph' || parameter?.inputType === 'document' ? (
                                    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, '& .ck-editor__editable': { minHeight: '100px' } }}>
                                      <CKEditor
                                        editor={ClassicEditor}
                                        config={editorConfiguration}
                                        data={typeof displayValue === 'object' ? (displayValue._summary || '') : (displayValue || '')}
                                        onChange={(event, editor) => {
                                          const data = editor.getData();
                                          if (parameter?.isGrid) {
                                            const currentVal = typeof displayValue === 'object' ? displayValue : { _summary: displayValue || '' };
                                            handleResultChange(result._id, { ...currentVal, _summary: data });
                                          } else {
                                            handleResultChange(result._id, data);
                                          }
                                        }}
                                        disabled={!canEditDates()}
                                      />
                                    </Box>
                                  ) : (
                                    <TextField
                                      fullWidth
                                      size="small"
                                      variant="outlined"
                                      value={typeof displayValue === 'object' ? (displayValue._summary || '') : (displayValue || '')}
                                      onChange={(e) => {
                                        if (parameter?.isGrid) {
                                          const currentVal = typeof displayValue === 'object' ? displayValue : { _summary: displayValue || '' };
                                          handleResultChange(result._id, { ...currentVal, _summary: e.target.value });
                                        } else {
                                          handleResultChange(result._id, e.target.value);
                                        }
                                      }}
                                      disabled={isCalculated || !canEditDates()}
                                      placeholder={isCalculated ? "Auto-calculated" : parameter?.isGrid ? "Overall result (e.g. Positive)" : "Enter value"}
                                      type={parameter?.inputType === 'numeric' ? 'number' : 'text'}
                                      error={result.status !== 'normal' && result.status !== undefined}
                                      inputProps={{ step: "any", min: 0 }}
                                    />
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {safeRender(result.unit || parameter?.unit)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {renderReferenceRangeCell(parameter, result)}
                                </TableCell>
                                <TableCell>
                                  <Tooltip title={`Status: ${result.status}`}>
                                    <span>
                                      {getResultStatusChip(result.status)}
                                    </span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>
                                  {isCalculated ? (
                                    <Chip
                                      label="Calculated"
                                      size="small"
                                      color="info"
                                      icon={<Calculate />}
                                    />
                                  ) : (
                                    <Chip
                                      label="Input"
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <TableContainer component={Paper} elevation={1}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: 'primary.main' }}>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Parameter</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Result</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Unit</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Reference Range</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedService.results?.map((result) => {
                            const paramId = result.parameter?._id || result.parameter;
                            const parameter = selectedService.service?.parameters?.find(
                              p => p._id === paramId
                            );

                            return (
                              <TableRow key={result._id} hover>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="medium">
                                    {safeRender(parameter?.label) || 'Unknown Parameter'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {parameter?.isGrid ? (
                                    <Box sx={{ mt: 1, border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                                            <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Label</TableCell>
                                            {parameter.gridConfig?.columns?.map((col, i) => (
                                              <TableCell key={i} align="center" sx={{ fontWeight: 'bold', py: 0.5 }}>{col}</TableCell>
                                            ))}
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {parameter.gridConfig?.rows?.map((row, i) => (
                                            <TableRow key={i}>
                                              <TableCell sx={{ py: 0.5, fontWeight: 'medium' }}>{row}</TableCell>
                                              {parameter.gridConfig.columns.map((col, j) => (
                                                <TableCell key={j} align="center" sx={{ py: 0.5 }}>
                                                  <Typography variant="body2">
                                                    {result.value?.[row]?.[col] || '-'}
                                                  </Typography>
                                                </TableCell>
                                              ))}
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  ) : (
                                    <Typography variant="body2">
                                      {(() => {
                                        const val = result.value;
                                        if (!val) return 'N/A';
                                        if (typeof val === 'object') {
                                          try {
                                            if (parameter?.inputType === 'grid') {
                                              return 'Grid Data';
                                            }
                                            if (Object.keys(val).length > 0) {
                                              return Object.keys(val).join(', ');
                                            }
                                            return JSON.stringify(val);
                                          } catch (e) {
                                            return 'Complex Result';
                                          }
                                        }
                                        return val;
                                      })()}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {safeRender(result.unit)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {renderReferenceRangeCell(parameter, result)}
                                </TableCell>
                                <TableCell>
                                  <Tooltip title={`Status: ${result.status}`}>
                                    <span>
                                      {getResultStatusChip(result.status)}
                                    </span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>
                                  {parameter?.isCalculated ? (
                                    <Chip
                                      label="Calculated"
                                      size="small"
                                      color="info"
                                      icon={<Calculate />}
                                    />
                                  ) : (
                                    <Chip
                                      label="Input"
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    {selectedService?.cancelled ? (
                      <>
                        <Block sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
                        <Typography variant="h6" color="error.main" gutterBottom>
                          Test Service Cancelled
                        </Typography>
                        {selectedService.cancellationReason && (
                          <Alert severity="info" sx={{ mt: 2, maxWidth: 400, mx: 'auto' }}>
                            {selectedService.cancellationReason}
                          </Alert>
                        )}
                      </>
                    ) : selectedService?.deleted ? (
                      <>
                        <Delete sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          Test Service Deleted
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Science sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                          Select a test from the sidebar to view results
                        </Typography>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>

        {/* ==================== DIALOGS ==================== */}

        {/* Save Dialog */}
        <Dialog open={saveDialog} onClose={() => setSaveDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Save Results</DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              Are you sure you want to save the results for {selectedService?.service?.test_name}?
            </Typography>
            <Alert severity="info" sx={{ mt: 2 }}>
              Results will be saved as "In Progress" and can be modified later.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={saveResults} disabled={saving}>
              {saving ? 'Saving...' : 'Save Results'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Verification Dialog */}
        <Dialog open={verificationDialog} onClose={() => setVerificationDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VerifiedUser color="primary" />
              Complete Test Results
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to mark <strong>{selectedService?.service?.test_name}</strong> as completed?
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              Once completed, results will be sent for verification and cannot be modified.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVerificationDialog(false)}>Cancel</Button>
            <Button variant="contained" color="success" onClick={markAsCompleted} disabled={saving}>
              {saving ? 'Completing...' : 'Mark as Completed'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Date Edit Dialog */}
        <Dialog open={dateEditDialog} onClose={() => setDateEditDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday color="primary" />
              Edit {selectedService?.service?.test_name} Dates
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Service: {selectedService?.service?.test_name}
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <DatePicker
                label="Collection Date"
                value={editDates.collectionDate}
                onChange={(newValue) => setEditDates(prev => ({ ...prev, collectionDate: newValue }))}
                renderInput={(params) => <TextField {...params} fullWidth />}
                format="DD/MM/YYYY HH:mm"
              />
              <DatePicker
                label="Received Date"
                value={editDates.receivedDate}
                onChange={(newValue) => setEditDates(prev => ({ ...prev, receivedDate: newValue }))}
                renderInput={(params) => <TextField {...params} fullWidth />}
                format="DD/MM/YYYY HH:mm"
              />
              <DatePicker
                label="Tested Date"
                value={editDates.testedAt}
                onChange={(newValue) => setEditDates(prev => ({ ...prev, testedAt: newValue }))}
                renderInput={(params) => <TextField {...params} fullWidth />}
                format="DD/MM/YYYY HH:mm"
              />
              <DatePicker
                label="Verified Date"
                value={editDates.verifiedAt}
                onChange={(newValue) => setEditDates(prev => ({ ...prev, verifiedAt: newValue }))}
                renderInput={(params) => <TextField {...params} fullWidth />}
                format="DD/MM/YYYY HH:mm"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDateEditDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={saveEditedDates} disabled={saving}>
              {saving ? 'Saving...' : 'Save Service Dates'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Dialog */}
        <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
              <Block />
              Cancel Test Report
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Test cancellation will mark the test as cancelled and stop further processing.
            </Alert>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to cancel the test for:
            </Typography>
            <Typography variant="h6" color="primary" gutterBottom>
              {resultData?.patient_name} - {resultData?.barcode_no}
            </Typography>
            <TextField
              fullWidth
              label="Cancellation Reason"
              multiline
              rows={3}
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              placeholder="Enter reason for cancellation..."
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialog(false)}>No, Keep Active</Button>
            <Button variant="contained" color="warning" onClick={cancelTestReport} disabled={saving || !cancellationReason.trim()}>
              {saving ? 'Cancelling...' : 'Yes, Cancel Test'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
              <Delete />
              Delete Test Report
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              This action cannot be undone!
            </Alert>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to delete the test report for:
            </Typography>
            <Typography variant="h6" color="primary" gutterBottom>
              {resultData?.patient_name} - {resultData?.barcode_no}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              All test results and associated data will be permanently removed.
            </Typography>
            <TextField
              fullWidth
              label="Deletion Reason"
              multiline
              rows={3}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Enter reason for deletion..."
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={deleteTestReport} disabled={saving || !deleteReason.trim()}>
              {saving ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Restore Dialog */}
        <Dialog open={restoreDialog} onClose={() => setRestoreDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
              <Refresh />
              Restore Test Report
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              This will restore the test report to its previous state.
            </Alert>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to restore the test report for:
            </Typography>
            <Typography variant="h6" color="primary" gutterBottom>
              {resultData?.patient_name} - {resultData?.barcode_no}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRestoreDialog(false)}>Cancel</Button>
            <Button variant="contained" color="success" onClick={restoreTestReport} disabled={saving}>
              {saving ? 'Restoring...' : 'Yes, Restore'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Date History Dialog */}
        <Dialog open={dateHistoryDialog} onClose={() => setDateHistoryDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <History color="primary" />
              Date Edit History
            </Box>
          </DialogTitle>
          <DialogContent>
            <TableContainer component={Paper} elevation={0} sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Field</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>Old Value</TableCell>
                    <TableCell>New Value</TableCell>
                    <TableCell>Edited By</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dateHistory.map((edit, index) => (
                    <TableRow key={index}>
                      <TableCell><Typography variant="body2">{edit.field?.replace('_', ' ')}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{edit.serviceId ? 'Service ' + edit.serviceId.substring(0, 8) : 'General'}</Typography></TableCell>
                      <TableCell><Typography variant="body2" color="textSecondary">{edit.oldValue ? formatDate(edit.oldValue) : 'N/A'}</Typography></TableCell>
                      <TableCell><Typography variant="body2" color="primary">{edit.newValue ? formatDate(edit.newValue) : 'N/A'}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{edit.editedBy?.name || 'System'}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{edit.editedAt ? formatDate(edit.editedAt) : 'N/A'}</Typography></TableCell>
                      <TableCell><Typography variant="body2" color="textSecondary">{edit.reason || 'N/A'}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDateHistoryDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Doctor Selection Dialog */}
        {doctorSelectionDialog && renderDoctorSelectionDialog()}

        {/* Action Menu */}
        <Menu anchorEl={actionMenuAnchor} open={actionMenuOpen} onClose={handleActionMenuClose}>
          <MenuItem onClick={() => { handleActionMenuClose(); setCancelDialog(true); }} disabled={!canCancelOrDelete()}>
            <ListItemIcon><Block fontSize="small" /></ListItemIcon>
            <ListItemText>Cancel Test</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { handleActionMenuClose(); setDeleteDialog(true); }} disabled={!canCancelOrDelete()}>
            <ListItemIcon><Delete fontSize="small" /></ListItemIcon>
            <ListItemText>Delete Test</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { handleActionMenuClose(); loadDateHistory(); }}>
            <ListItemIcon><History fontSize="small" /></ListItemIcon>
            <ListItemText>Date History</ListItemText>
          </MenuItem>
        </Menu>

        {/* Snackbar */}
        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};